import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { Add, Delete, Edit, Refresh, Search } from "@mui/icons-material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";

import {
  createTimeOffice,
  deleteTimeOffice,
  getTimeOfficeList,
  getTimeOfficeNextSrNo,
  updateTimeOffice,
} from "../../../services/timeOfficeService";

import { currentMonth } from "../../../services/dashBoardService";
import PageHeader from "../../../utils/PageHeader";

// ======================================================
// EMPLOYEE CATEGORIES
// ======================================================
// IMPORTANT DATABASE MAPPING:
//
// Employee Category -> CEMPTYPE
// Employee Type     -> empcategory
//
// CEMPTYPE:
//   1 = JE / Sr Technician / MCM / GR-I
//   3 = GR-II / GR-III
//   5 = Helper
// ======================================================

const employeeCategories = [
  {
    label: "JE",
    value: "JE",
    cemptype: 1,
  },
  {
    label: "Sr Technician",
    value: "Sr Technician",
    cemptype: 1,
  },
  {
    label: "MCM",
    value: "MCM",
    cemptype: 1,
  },
  {
    label: "GR-I",
    value: "GR-I",
    cemptype: 1,
  },
  {
    label: "GR-II",
    value: "GR-II",
    cemptype: 3,
  },
  {
    label: "GR-III",
    value: "GR-III",
    cemptype: 3,
  },
  {
    label: "Helper",
    value: "Helper",
    cemptype: 5,
  },
];

// ======================================================
// EMPLOYEE TYPES
// ======================================================

const employeeTypes = [
  {
    label: "DW",
    value: "DW",
  },
  {
    label: "EIW",
    value: "EIW",
  },
  {
    label: "NIW",
    value: "NIW",
  },
];

// ======================================================
// INITIAL FORM
// ======================================================

const emptyForm = {
  srno: "",
  EmployeeNumber: "",
  ShopCode: "",
  WorkOrder: "",
  Hr: 0,
  Mint: 0,

  // UI fields
  employeeType: "DW",
  employeeCategory: "JE",
};

// ======================================================
// CATEGORY -> CEMPTYPE
// ======================================================

const getCempTypeFromCategory = (category) => {
  const item = employeeCategories.find((x) => x.value === category);

  return item?.cemptype ?? null;
};

// ======================================================
// CEMPTYPE -> CATEGORY
// ======================================================

const getCategoryFromCempType = (cemptype) => {
  const type = Number(cemptype);

  const item = employeeCategories.find((x) => x.cemptype === type);

  return item?.value || "JE";
};

// ======================================================
// DATABASE -> CATEGORY
// ======================================================
// Priority:
// 1. employeepost  -> JE / Sr Technician / MCM / GR-I...
// 2. CEMPTYPE      -> fallback for old records
// ======================================================

const getEmployeeCategoryForEdit = (row) => {
  // First preference: employeepost
  if (row?.employeepost) {
    const item = employeeCategories.find((x) => x.value === row.employeepost);

    if (item) {
      return item.value;
    }
  }

  // Fallback for old records where employeepost is NULL
  return getCategoryFromCempType(row?.CEMPTYPE);
};

// ======================================================
// COMPONENT
// ======================================================

const TimeOffice = () => {
  const [monthData, setMonthData] = useState(null);

  const [form, setForm] = useState(emptyForm);

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [search, setSearch] = useState("");

  const [searchInput, setSearchInput] = useState("");

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [totalRows, setTotalRows] = useState(0);

  const [deleteDialog, setDeleteDialog] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ====================================================
  // REFS
  // ====================================================

  const ticketRef = useRef(null);
  const shopRef = useRef(null);
  const workOrderRef = useRef(null);
  const hoursRef = useRef(null);
  const employeeTypeRef = useRef(null);
  const employeeCategoryRef = useRef(null);
  const minutesRef = useRef(null);
  const saveRef = useRef(null);

  // ====================================================
  // MESSAGE
  // ====================================================

  const showMessage = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  // ====================================================
  // ENTER KEY NAVIGATION
  // ====================================================

  const handleEnterNext = (event, nextRef, submit = false) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    event.stopPropagation();

    if (submit) {
      saveRef.current?.click();
      return;
    }

    nextRef?.current?.focus();
  };

  // ====================================================
  // CURRENT MONTH
  // ====================================================

  const loadCurrentMonth = useCallback(async () => {
    try {
      const res = await currentMonth();

      setMonthData(res?.data?.data || null);
    } catch (err) {
      console.error("Current month error:", err);

      showMessage("Unable to load current month", "error");
    }
  }, [showMessage]);

  // ====================================================
  // MONTH
  // ====================================================

  const currentMonthNumber = useMemo(() => {
    if (monthData?.month != null) {
      return Number(monthData.month);
    }

    return new Date().getMonth() + 1;
  }, [monthData?.month]);

  // ====================================================
  // YEAR
  // ====================================================

  const currentYearNumber = useMemo(() => {
    if (monthData?.year != null) {
      return Number(monthData.year);
    }

    return new Date().getFullYear();
  }, [monthData?.year]);

  // ====================================================
  // MONTH NAME
  // ====================================================

  const monthName = useMemo(() => {
    return new Date(
      currentYearNumber,
      currentMonthNumber - 1,
      1,
    ).toLocaleString("en-US", {
      month: "long",
    });
  }, [currentMonthNumber, currentYearNumber]);

  // ====================================================
  // LOAD NEXT SR NO
  // ====================================================

  const loadNextSrNo = useCallback(async () => {
    try {
      const res = await getTimeOfficeNextSrNo();

      setForm((prev) => ({
        ...prev,
        srno: res?.srno ?? "",
      }));
    } catch (err) {
      console.error("Next srno error:", err);

      showMessage("Unable to get next serial number", "error");
    }
  }, [showMessage]);

  // ====================================================
  // LOAD LIST
  // ====================================================

  const loadList = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getTimeOfficeList({
        page: page + 1,
        limit: rowsPerPage,
        search,
        month: currentMonthNumber,
        year: currentYearNumber,
      });




      setRows(res?.data || []);
      console.log("Time Office list response:", res);
      setTotalRows(Number(res?.total || 0));
    } catch (err) {
      console.error("Time Office list error:", err);

      showMessage("Unable to load data", "error");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    search,
    currentMonthNumber,
    currentYearNumber,
    showMessage,
  ]);

  // ====================================================
  // INITIAL LOAD
  // ====================================================

  useEffect(() => {
    loadCurrentMonth();
  }, [loadCurrentMonth]);

  // ====================================================
  // LOAD LIST
  // ====================================================

  useEffect(() => {
    if (currentMonthNumber && currentYearNumber) {
      loadList();
    }
  }, [loadList, currentMonthNumber, currentYearNumber]);

  // ====================================================
  // INITIAL SERIAL NUMBER
  // ====================================================

  useEffect(() => {
    loadNextSrNo();
  }, [loadNextSrNo]);

  // ====================================================
  // RESET FORM
  // ====================================================

  // const resetForm = async (focusTicket = true) => {
  //   setEditMode(false);

  //   // Preserve selected type/category
  //   // for fast continuous entry.
  //   setForm((prev) => ({
  //     ...emptyForm,
  //     employeeType: prev.employeeType || "DW",
  //     employeeCategory: prev.employeeCategory || "JE",
  //   }));

  //   await loadNextSrNo();

  //   if (focusTicket) {
  //     setTimeout(() => {
  //       ticketRef.current?.focus();
  //       ticketRef.current?.select?.();
  //     }, 100);
  //   }
  // };

  const resetForm = async (focusTicket = true, preserveEntryFields = false) => {
    setEditMode(false);

    if (preserveEntryFields) {
      // After Save:
      // Keep Shop Code, Work Order, Employee Type and Employee Category
      // Clear Ticket Number and reset Hours/Minutes.
      setForm((prev) => ({
        ...emptyForm,

        srno: "",
        EmployeeNumber: "",
        ShopCode: prev.ShopCode || "",
        WorkOrder: prev.WorkOrder || "",
        Hr: 0,
        Mint: 0,

        employeeType: prev.employeeType || "DW",
        employeeCategory: prev.employeeCategory || "JE",
      }));
    } else {
      // Normal Clear / Cancel Edit
      setForm((prev) => ({
        ...emptyForm,
        employeeType: prev.employeeType || "DW",
        employeeCategory: prev.employeeCategory || "JE",
      }));
    }

    // Always get the next Sr No
    await loadNextSrNo();

    if (focusTicket) {
      setTimeout(() => {
        ticketRef.current?.focus();
        ticketRef.current?.select?.();
      }, 100);
    }
  };

  // ====================================================
  // FORM CHANGE
  // ====================================================

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // ====================================================
  // SAVE / UPDATE
  // ====================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    // --------------------------------------------------
    // VALIDATION
    // --------------------------------------------------

    if (!form.EmployeeNumber) {
      showMessage("Please enter Employee/Ticket Number", "error");
      return;
    }

    if (!form.ShopCode) {
      showMessage("Please enter Shop Code", "error");
      return;
    }

    if (!form.WorkOrder) {
      showMessage("Please enter Work Order", "error");
      return;
    }

    if (!form.employeeType) {
      showMessage("Please select Employee Type", "error");
      return;
    }

    if (!form.employeeCategory) {
      showMessage("Please select Employee Category", "error");
      return;
    }

    const minutes = Number(form.Mint);

    if (minutes < 0 || minutes > 59) {
      showMessage("Minutes must be between 0 and 59", "error");
      return;
    }

    const cempType = getCempTypeFromCategory(form.employeeCategory);

    if (cempType == null) {
      showMessage("Invalid Employee Category", "error");
      return;
    }

    setSaving(true);

    try {
      // ==================================================
      // IMPORTANT DATABASE MAPPING
      //
      // UI Employee Type
      //       ↓
      // database empcategory
      //
      // UI Employee Category
      //       ↓
      // database CEMPTYPE
      // ==================================================

      const payload = {
        EmployeeNumber: form.EmployeeNumber,

        ShopCode: form.ShopCode,

        WorkOrder: form.WorkOrder,

        Hr: Number(form.Hr) || 0,

        Mint: Number(form.Mint) || 0,

        // ----------------------------------------------
        // CORRECT MAPPING
        // ----------------------------------------------

        empcategory: form.employeeType,

        // Employee Category VALUE
        employeepost: form.employeeCategory,

        CEMPTYPE: cempType,
      };

      // ------------------------------------------------
      // MONTH / YEAR
      // ------------------------------------------------

      if (monthData?.month != null && monthData?.year != null) {
        payload.month = monthData.month;

        payload.year = monthData.year;
      }

      // ------------------------------------------------
      // DEBUG
      // ------------------------------------------------

      //console.log("Time Office payload:", payload);

      // ------------------------------------------------
      // UPDATE
      // ------------------------------------------------

      if (editMode) {
        await updateTimeOffice(form.srno, payload);

        showMessage("Entry updated successfully", "success");
      }

      // ------------------------------------------------
      // CREATE
      // ------------------------------------------------
      else {
        await createTimeOffice(payload);

        showMessage("Entry saved successfully", "success");
      }

      // ------------------------------------------------
      // REFRESH
      // ------------------------------------------------

      setPage(0);

      // Reset form and get new serial
      //await resetForm();
      await resetForm(true, true);

      // loadList uses current page/search state.
      // Call it after save/update to immediately
      // refresh the visible data.
      await loadList();
    } catch (err) {
      console.error("Save Time Office error:", err);

      showMessage(
        err?.response?.data?.message || "Unable to save entry",
        "error",
      );
    } finally {
      setSaving(false);

      setTimeout(() => {
        ticketRef.current?.focus();
        ticketRef.current?.select?.();
      }, 100);
    }
  };

  // ====================================================
  // EDIT
  // ====================================================

  const handleEdit = (row) => {
    setEditMode(true);

    // ==================================================
    // CORRECT DATABASE -> FORM MAPPING
    //
    // row.empcategory -> employeeType
    // row.CEMPTYPE    -> employeeCategory
    // ==================================================

    const employeeType = row?.empcategory || "DW";

    //const employeeCategory = getCategoryFromCempType(row?.CEMPTYPE);
    const employeeCategory = getEmployeeCategoryForEdit(row);
    // console.log("Editing Time Office row:", {
    //   row,
    //   employeeType,
    //   employeeCategory,
    // });

    setForm({
      ...emptyForm,

      srno: row?.srno ?? "",

      EmployeeNumber: row?.EmployeeNumber ?? "",

      ShopCode: row?.ShopCode ?? "",

      WorkOrder: row?.WorkOrder ?? "",

      Hr: row?.Hr ?? 0,

      Mint: row?.Mint ?? 0,

      // Correct mapping
      employeeType,

      employeeCategory,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setTimeout(() => {
      ticketRef.current?.focus();
      ticketRef.current?.select?.();
    }, 300);
  };

  // ====================================================
  // DELETE DIALOG
  // ====================================================

  const openDeleteDialog = (row) => {
    setSelectedRow(row);
    setDeleteDialog(true);
  };

  // ====================================================
  // DELETE
  // ====================================================

  const handleDelete = async () => {
    if (!selectedRow) {
      return;
    }

    if (!monthData?.month || !monthData?.year) {
      showMessage("Month and year are required", "error");
      return;
    }

    try {
      await deleteTimeOffice(selectedRow.srno, {
        month: monthData.month,
        year: monthData.year,
      });

      showMessage("Entry deleted successfully", "success");

      setDeleteDialog(false);
      setSelectedRow(null);

      setPage(0);

      await loadList();
      await loadNextSrNo();
    } catch (err) {
      console.error("Delete error:", err);

      showMessage(
        err?.response?.data?.message || "Unable to delete entry",
        "error",
      );
    }
  };

  // ====================================================
  // SEARCH
  // ====================================================

  const handleSearch = () => {
    setPage(0);

    setSearch(searchInput.trim());
  };

  // ====================================================
  // CLEAR SEARCH
  // ====================================================

  const handleClearSearch = () => {
    setSearchInput("");

    setSearch("");

    setPage(0);
  };

  // ====================================================
  // REFRESH
  // ====================================================

  const handleRefresh = async () => {
    setSearchInput("");

    setSearch("");

    setPage(0);

    await loadList();
    await loadNextSrNo();
  };

  // ====================================================
  // CATEGORY DISPLAY
  // ====================================================

  const getCategoryName = (cemptype) => {
    const item = employeeCategories.find(
      (x) => x.cemptype === Number(cemptype),
    );

    return item?.label || "-";
  };

  // ====================================================
  // RENDER
  // ====================================================

  return (
    <Box
      sx={{
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <PageHeader
        title="Time Office Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${currentYearNumber}`}
        icon={AccessTimeIcon}
        statusChip={{
          label: loading
            ? "Loading..."
            : totalRows > 0
              ? `${totalRows} Data Entered`
              : "No Data",
          color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        }}
        showRefresh
        onRefresh={handleRefresh}
      />

      {/* =================================================
          ENTRY FORM
      ================================================= */}

      <Card
        elevation={2}
        sx={{
          mb: 2,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {/* ========================================
                  LEFT
              ======================================== */}

              <Stack spacing={2}>
                {/* SR NO */}

                <TextField
                  label="Sr No"
                  value={form.srno}
                  size="small"
                  fullWidth
                  disabled
                />

                {/* TICKET NUMBER */}

                <TextField
                  inputRef={ticketRef}
                  label="Ticket Number"
                  value={form.EmployeeNumber}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (/^\d*$/.test(value)) {
                      setForm((prev) => ({
                        ...prev,
                        EmployeeNumber: value,
                      }));
                    }
                  }}
                  onKeyDown={(e) => handleEnterNext(e, shopRef)}
                  size="small"
                  fullWidth
                  required
                  autoComplete="off"
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 10,
                    },
                  }}
                />

                {/* WORK ORDER */}

                <TextField
                  inputRef={workOrderRef}
                  label="Work Order"
                  value={form.WorkOrder}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (/^\d*$/.test(value)) {
                      setForm((prev) => ({
                        ...prev,
                        WorkOrder: value,
                      }));
                    }
                  }}
                  onKeyDown={(e) => handleEnterNext(e, employeeCategoryRef)}
                  size="small"
                  fullWidth
                  required
                  autoComplete="off"
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 8,
                    },
                  }}
                />

                {/* HOURS */}

                <TextField
                  inputRef={hoursRef}
                  label="Hours"
                  type="number"
                  value={form.Hr}
                  onChange={handleChange("Hr")}
                  onKeyDown={(e) => handleEnterNext(e, minutesRef)}
                  size="small"
                  fullWidth
                  inputProps={{
                    min: 0,
                  }}
                  autoComplete="off"
                />
              </Stack>

              {/* ========================================
                  RIGHT
              ======================================== */}

              <Stack spacing={2}>
                {/* EMPLOYEE TYPE */}

                <TextField
                  inputRef={employeeTypeRef}
                  select
                  label="Employee Type"
                  value={form.employeeType}
                  onChange={handleChange("employeeType")}
                  onKeyDown={(e) => handleEnterNext(e, ticketRef)}
                  size="small"
                  fullWidth
                >
                  {employeeTypes.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* SHOP CODE */}

                <TextField
                  inputRef={shopRef}
                  label="Shop Code"
                  type="number"
                  value={form.ShopCode}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (/^\d*$/.test(value)) {
                      setForm((prev) => ({
                        ...prev,
                        ShopCode: value,
                      }));
                    }
                  }}
                  onKeyDown={(e) => handleEnterNext(e, workOrderRef)}
                  size="small"
                  fullWidth
                  required
                  autoComplete="off"
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 3,
                    },
                  }}
                />

                {/* EMPLOYEE CATEGORY */}

                <TextField
                  inputRef={employeeCategoryRef}
                  select
                  label="Employee Category"
                  value={form.employeeCategory}
                  onChange={handleChange("employeeCategory")}
                  onKeyDown={(e) => handleEnterNext(e, hoursRef)}
                  size="small"
                  fullWidth
                >
                  {employeeCategories.map((item) => (
                    <MenuItem
                      key={`${item.value}-${item.ShopCode}`}
                      value={item.value}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* MINUTES */}

                <TextField
                  inputRef={minutesRef}
                  select
                  label="Minutes"
                  value={form.Mint}
                  onChange={handleChange("Mint")}
                  onKeyDown={(e) => handleEnterNext(e, saveRef)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value={0}>0</MenuItem>

                  <MenuItem value={30}>30</MenuItem>
                </TextField>
              </Stack>
            </Box>

            {/* =================================================
                BUTTONS
            ================================================= */}

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={2}
              justifyContent="center"
              sx={{
                mt: 3,
              }}
            >
              <Button
                ref={saveRef}
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : editMode ? (
                    <Edit />
                  ) : (
                    <Add />
                  )
                }
                sx={{
                  minWidth: 160,
                  bgcolor: "#fff500",
                  color: "#000",
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: "#e6da00",
                  },
                }}
              >
                {saving ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={() => resetForm()}
                sx={{
                  minWidth: 160,
                  fontWeight: 700,
                }}
              >
                {editMode ? "Cancel Edit" : "Clear"}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* =================================================
          LIST
      ================================================= */}

      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
        }}
      >
        <CardContent>
          {/* SEARCH TOOLBAR */}

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1}
            sx={{
              mb: 2,
            }}
          >
            <TextField
              label="Search"
              placeholder="Sr No / Employee / Shop / Work Order"
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              sx={{
                flex: 1,
              }}
            />

            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleSearch}
            >
              Search
            </Button>

            <Button variant="outlined" onClick={handleClearSearch}>
              Clear
            </Button>

            <IconButton onClick={handleRefresh} color="primary" title="Refresh">
              <Refresh />
            </IconButton>
          </Stack>

          {/* ROW COUNT */}

          <Typography
            sx={{
              mb: 1,
              fontWeight: 700,
            }}
          >
            No of Rows:{" "}
            <Box
              component="span"
              sx={{
                color: "error.main",
                ml: 1,
              }}
            >
              {totalRows}
            </Box>
          </Typography>

          {/* TABLE */}

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 550,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sr No</TableCell>

                  <TableCell>Shop Code</TableCell>

                  <TableCell>Employee Number</TableCell>

                  <TableCell>Employee Category</TableCell>

                  <TableCell>Employee Type</TableCell>

                  <TableCell>Work Order</TableCell>

                  <TableCell align="right">Hours</TableCell>

                  <TableCell align="right">Minutes</TableCell>

                  <TableCell align="right">Entry User</TableCell>

                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{
                        py: 5,
                      }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={`row-${row.srno}-${row.ShopCode}`} hover>
                      <TableCell>{row.srno}</TableCell>

                      <TableCell>{row.ShopCode}</TableCell>

                      <TableCell>{row.EmployeeNumber}</TableCell>

                      {/* =================================
                          EMPLOYEE CATEGORY
                          DATABASE: CEMPTYPE
                      ================================= */}

                      <TableCell>
                        {/* {getCategoryName(row.CEMPTYPE)} ({row.CEMPTYPE}) */}
                        {row.employeepost}
                      </TableCell>

                      {/* =================================
                          EMPLOYEE TYPE
                          DATABASE: empcategory
                      ================================= */}

                      <TableCell>{row.empcategory || "-"}</TableCell>

                      <TableCell>{row.WorkOrder}</TableCell>

                      <TableCell align="right">{row.Hr}</TableCell>

                      <TableCell align="right">{row.Mint}</TableCell>

                      <TableCell align="right">{row.uname}</TableCell>

                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(row)}
                          title="Edit"
                          size="small"
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          color="error"
                          onClick={() => openDeleteDialog(row)}
                          title="Delete"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}

          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_event, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));

              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      {/* =================================================
          DELETE DIALOG
      ================================================= */}

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Entry</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete Sr No{" "}
            <strong>{selectedRow?.srno}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>

          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeOffice;
